/**
 * TimingBarChart Component
 *
 * Displays a horizontal bar chart showing timing breakdown for each profiled operation.
 * Each bar represents the duration of an operation with color coding based on relative time.
 */

import React, {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {ProfileSession} from '../../@types/profiler';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface TimingBarChartProps {
    session: ProfileSession;
    height?: number;
}

/**
 * Get color based on percentage of total time
 */
const getBarColor = (percentage: number): string => {
    if (percentage >= 40) return 'rgba(244, 67, 54, 0.8)';      // Red - critical
    if (percentage >= 25) return 'rgba(255, 152, 0, 0.8)';     // Orange - warning
    if (percentage >= 15) return 'rgba(255, 193, 7, 0.8)';     // Amber - notable
    if (percentage >= 5) return 'rgba(76, 175, 80, 0.8)';      // Green - normal
    return 'rgba(158, 158, 158, 0.8)';                          // Grey - minimal
};

const TimingBarChart: React.FC<TimingBarChartProps> = ({ session, height = 250 }) => {
    const chartData = useMemo(() => {
        const labels = session.entries.map(e => e.label);
        const data = session.entries.map(e => e.duration);
        const backgroundColors = session.entries.map(e => getBarColor(e.percentage));

        return {
            labels,
            datasets: [{
                label: 'Duration (ms)',
                data,
                backgroundColor: backgroundColors,
                borderColor: backgroundColors.map(c => c.replace('0.8', '1')),
                borderWidth: 1,
                borderRadius: 4,
            }]
        };
    }, [session]);

    const options = useMemo(() => ({
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Timing Breakdown',
                font: {
                    size: 14,
                    weight: 600 as const
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: { parsed: { x: number }; dataIndex: number }): string => {
                        const entry = session.entries[context.dataIndex];
                        return `${entry.duration.toFixed(1)}ms (${entry.percentage.toFixed(1)}%)`;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Duration (ms)',
                    font: {
                        size: 11
                    }
                },
                ticks: {
                    font: {
                        size: 10
                    }
                }
            },
            y: {
                ticks: {
                    font: {
                        size: 10
                    }
                }
            }
        },
        animation: {
            duration: 300
        }
    }), [session]);

    // Custom plugin to draw values at end of bars
    const dataLabelsPlugin = useMemo(() => ({
        id: 'dataLabels',
        afterDatasetsDraw(chart: ChartJS): void {
            const ctx = chart.ctx;
            chart.data.datasets.forEach((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                    const entry = session.entries[index];
                    if (entry) {
                        ctx.fillStyle = '#666';
                        ctx.font = '10px sans-serif';
                        ctx.textAlign = 'left';
                        ctx.textBaseline = 'middle';
                        const barElement = bar as { x: number; y: number };
                        ctx.fillText(
                            `${entry.duration.toFixed(1)}ms`,
                            barElement.x + 5,
                            barElement.y
                        );
                    }
                });
            });
        }
    }), [session]);

    if (!session.entries.length) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No timing data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height, p: 1 }}>
            <Bar data={chartData} options={options} plugins={[dataLabelsPlugin]} />
        </Box>
    );
};

export default React.memo(TimingBarChart);
